import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import authApi from 'ecommerce/api/auth';
import { AUTH_ERROR_TYPE, authErrorType } from 'ecommerce/lib/authError';
import { useApiHandler } from 'ecommerce/lib/useApi';
import { isEmailValid } from 'utils/validator';

import { PrivacyPageLink, TermsPageLink } from '../pageLinks';
import {
  BackLink,
  CalloutAction,
  Divider,
  EmailInput,
  ErrorCallout,
  Field,
  Heading,
  MIN_PASSWORD_LENGTH,
  Note,
  PasswordHint,
  PasswordInput,
} from './parts';

// Sign in, create account, forgot password and check your email (Auth 1-6, docs/auth-flow.md).
// The mode lives in state, not in the URL: withAuth renders this in place of protected pages and
// sign in returns to router.asPath, so a written ?mode= would end up in that redirect.

export const AUTH_MODE = Object.freeze({
  SIGN_IN: 'signin',
  SIGN_UP: 'signup',
  FORGOT: 'forgot',
  SENT: 'sent',
});

// Same as the resend cooldown the Pencil frame shows (Auth 6).
const RESEND_COOLDOWN_SECONDS = 60;

const EMAIL_ID = 'au-email';
const EMAIL_ERROR_ID = 'au-email-error';
const PASSWORD_ID = 'au-password';
const PASSWORD_ERROR_ID = 'au-password-error';
const PASSWORD_HINT_ID = 'au-password-hint';

const formatCountdown = (seconds) =>
  `${Math.floor(seconds / 60)}:${`${seconds % 60}`.padStart(2, '0')}`;

const useCountdown = () => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timer = setTimeout(() => setSeconds((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  return [seconds, () => setSeconds(RESEND_COOLDOWN_SECONDS)];
};

const AuthPanel = ({ initialMode, countryCode, amazon, onSignedIn, onTrackError, onTrack }) => {
  const [mode, setMode] = useState(
    Object.values(AUTH_MODE).includes(initialMode) && initialMode !== AUTH_MODE.SENT
      ? initialMode
      : AUTH_MODE.SIGN_IN,
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // { field: 'email' | 'password' | 'form', kind } drives which callout shows where.
  const [error, setError] = useState();
  const [cooldown, startCooldown] = useCountdown();
  const titleRef = useRef();
  const modeChanged = useRef(false);
  // Bumped on every mode switch. A response that arrives after the user left the screen
  // must not change the screen they are on now.
  const screenRef = useRef(0);
  // Where the reset link went. The email field can change while the request runs.
  const [sentTo, setSentTo] = useState('');
  // The ref blocks a second submit within the same render, `busy` disables the buttons.
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const api = authApi(countryCode);
  const isSignUp = mode === AUTH_MODE.SIGN_UP;

  useEffect(() => {
    if (!modeChanged.current) return;
    titleRef.current?.querySelector('h1')?.focus();
  }, [mode]);

  const switchMode = (next) => {
    modeChanged.current = true;
    screenRef.current += 1;
    setError();
    setPassword('');
    setMode(next);
  };

  const failValidation = (field, kind, errorType) => {
    setError({ field, kind });
    onTrackError(errorType, isSignUp);
  };

  const validateEmail = () => {
    if (!email) {
      failValidation('email', 'missing', AUTH_ERROR_TYPE.MISSING_FIELDS);
      return false;
    }
    if (!isEmailValid(email)) {
      failValidation('email', 'invalid', AUTH_ERROR_TYPE.INVALID_EMAIL);
      return false;
    }
    return true;
  };

  const signIn = useApiHandler(async () => {
    const screen = screenRef.current;
    const { data, error: apiError } = await api.signIn(email, password);
    // A successful sign in still counts after the user left the screen.
    if (data) {
      await onSignedIn(data, 'signin');
      return;
    }
    if (screen !== screenRef.current) return;

    if (apiError.code !== 401) {
      setError({ field: 'form', kind: 'signin' });
      onTrackError(authErrorType(apiError), false);
      return;
    }

    // Sign in answers one 401 for every failure. Ask whether the account exists, so the
    // message can point at the email or at the password.
    const { data: check } = await api.checkEmail(email);
    if (screen !== screenRef.current) return;
    if (check?.exists === false) {
      setError({ field: 'email', kind: 'unknown' });
      onTrackError(AUTH_ERROR_TYPE.UNKNOWN_EMAIL, false);
      return;
    }

    setError({ field: 'password', kind: check ? 'wrong' : 'mismatch' });
    onTrackError(AUTH_ERROR_TYPE.INVALID_CREDENTIALS, false);
  });

  const signUp = useApiHandler(async () => {
    const screen = screenRef.current;
    const { data, error: apiError } = await api.signUp(email, password);
    if (data) {
      await onSignedIn(data, 'signup');
      return;
    }
    if (screen !== screenRef.current) return;

    const errorType = authErrorType(apiError);
    setError(
      errorType === AUTH_ERROR_TYPE.EMAIL_TAKEN
        ? { field: 'email', kind: 'taken' }
        : { field: 'form', kind: 'signup' },
    );
    onTrackError(errorType, true);
  });

  const sendResetLink = useApiHandler(async () => {
    const screen = screenRef.current;
    const { error: apiError } = await api.resetPassword(email);
    if (screen !== screenRef.current) return;
    if (apiError) {
      setError(
        apiError === 404
          ? { field: 'email', kind: 'noPassword' }
          : { field: 'form', kind: 'reset' },
      );
      return;
    }

    onTrack('password_reset_requested');
    setSentTo(email);
    startCooldown();
    switchMode(AUTH_MODE.SENT);
  });

  const resend = useApiHandler(async () => {
    const screen = screenRef.current;
    const { error: apiError } = await api.resetPassword(sentTo);
    if (screen !== screenRef.current) return;
    if (apiError) {
      setError({ field: 'form', kind: 'reset' });
      return;
    }

    setError();
    onTrack('password_reset_requested', { resend: true });
    startCooldown();
  });

  const runExclusive = async (handler) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await handler();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError();

    if (!validateEmail()) return;

    if (mode === AUTH_MODE.FORGOT) {
      runExclusive(sendResetLink);
      return;
    }

    if (!password) {
      failValidation('password', 'missing', AUTH_ERROR_TYPE.MISSING_FIELDS);
      return;
    }

    if (isSignUp && password.length < MIN_PASSWORD_LENGTH) {
      failValidation('password', 'short', AUTH_ERROR_TYPE.INVALID_PASSWORD);
      return;
    }

    if (isSignUp) {
      runExclusive(signUp);
    } else {
      runExclusive(signIn);
    }
  };

  const emailError = error?.field === 'email' ? error.kind : undefined;
  const passwordError = error?.field === 'password' ? error.kind : undefined;
  const formError = error?.field === 'form' ? error.kind : undefined;

  const emailCallout = {
    missing: { title: 'Enter your email address.' },
    invalid: { title: 'This email address does not look right.', body: 'Check it for typos.' },
    unknown: {
      title: 'There is no Parabo account with this email.',
      body: 'Check it for typos, or create an account.',
      actions: (
        <CalloutAction onClick={() => switchMode(AUTH_MODE.SIGN_UP)}>
          Create an account
        </CalloutAction>
      ),
    },
    taken: {
      title: 'This email already has a Parabo account.',
      body: 'Sign in with this email, or reset the password if you do not remember it.',
      actions: (
        <>
          <CalloutAction onClick={() => switchMode(AUTH_MODE.SIGN_IN)}>
            Sign in instead
          </CalloutAction>
          <CalloutAction onClick={() => switchMode(AUTH_MODE.FORGOT)}>Reset password</CalloutAction>
        </>
      ),
    },
    noPassword: {
      title: 'There is no Parabo password for this email.',
      body: 'Check it for typos. If you signed in with Amazon before, use Sign in with Amazon.',
    },
  }[emailError];

  const resetAction = (
    <CalloutAction arrow onClick={() => switchMode(AUTH_MODE.FORGOT)}>
      Reset your password
    </CalloutAction>
  );

  const passwordCallout = {
    missing: { title: 'Enter your password.' },
    short: { title: `Use at least ${MIN_PASSWORD_LENGTH} characters.` },
    wrong: {
      title: 'That password does not match this email.',
      body: 'Check it and try again, or get a link to set a new password.',
      actions: resetAction,
    },
    // The account check failed, so do not claim the email exists.
    mismatch: {
      title: 'The email or password is incorrect.',
      body: 'Check them and try again, or get a link to set a new password.',
      actions: resetAction,
    },
  }[passwordError];

  const formCallout = {
    signin: 'We could not sign you in. Please try again.',
    signup: 'We could not create your account. Please try again.',
    reset: 'We could not send the link. Please try again.',
  }[formError];

  const emailField = (
    <Field id={EMAIL_ID} label="Email">
      <EmailInput
        id={EMAIL_ID}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        invalid={Boolean(emailCallout)}
        errorId={EMAIL_ERROR_ID}
        autoFocus={mode === AUTH_MODE.FORGOT}
      />
      {emailCallout && (
        <ErrorCallout id={EMAIL_ERROR_ID} title={emailCallout.title} actions={emailCallout.actions}>
          {emailCallout.body}
        </ErrorCallout>
      )}
    </Field>
  );

  const formAlert = formCallout && <ErrorCallout title={formCallout} />;

  if (mode === AUTH_MODE.SENT) {
    return (
      <div className="au-stack" ref={titleRef}>
        <BackLink onClick={() => switchMode(AUTH_MODE.SIGN_IN)} />
        <Heading icon="mail" title="Check your email">
          We sent a reset link to <strong className="au-email">{sentTo}</strong>. The link works for
          48 hours.
        </Heading>
        {formAlert}
        <div className="au-actions">
          <button
            type="button"
            className="au-btn au-btn--secondary"
            disabled={cooldown > 0 || busy}
            onClick={() => runExclusive(resend)}
          >
            {cooldown > 0 ? `Resend link in ${formatCountdown(cooldown)}` : 'Resend link'}
          </button>
          <div className="au-text-links">
            <button type="button" className="au-link" onClick={() => switchMode(AUTH_MODE.FORGOT)}>
              Use a different email
            </button>
          </div>
        </div>
        <Note icon="inbox">Nothing in your inbox? Check the spam and promotions folders.</Note>
      </div>
    );
  }

  if (mode === AUTH_MODE.FORGOT) {
    return (
      <form className="au-stack" ref={titleRef} onSubmit={handleSubmit} noValidate>
        <BackLink onClick={() => switchMode(AUTH_MODE.SIGN_IN)} />
        <Heading title="Reset your password">
          Enter the email you use for Parabo. We will send you a link to set a new password.
        </Heading>
        <div className="au-fields">{emailField}</div>
        {formAlert}
        <div className="au-actions">
          <button type="submit" className="au-btn" disabled={busy}>
            Send reset link
          </button>
        </div>
        <Note icon="info">
          Signed in with Amazon before? Your account has no Parabo password. Use Sign in with Amazon
          instead.
        </Note>
      </form>
    );
  }

  return (
    <form className="au-stack" ref={titleRef} onSubmit={handleSubmit} noValidate>
      {isSignUp ? (
        <Heading title="Create your account">
          Save your drafts, follow your orders and order again in a few clicks.
        </Heading>
      ) : (
        <Heading title="Sign in">Welcome back. Your drafts and orders are here.</Heading>
      )}

      <div className="au-fields">
        {emailField}
        <Field
          id={PASSWORD_ID}
          label="Password"
          aside={
            !isSignUp && (
              <button
                type="button"
                className="au-link au-link--sm"
                onClick={() => switchMode(AUTH_MODE.FORGOT)}
              >
                Forgot password?
              </button>
            )
          }
        >
          <PasswordInput
            id={PASSWORD_ID}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            invalid={Boolean(passwordCallout)}
            errorId={PASSWORD_ERROR_ID}
            hintId={isSignUp ? PASSWORD_HINT_ID : undefined}
          />
          {passwordCallout && (
            <ErrorCallout
              id={PASSWORD_ERROR_ID}
              title={passwordCallout.title}
              actions={passwordCallout.actions}
            >
              {passwordCallout.body}
            </ErrorCallout>
          )}
          {isSignUp && <PasswordHint id={PASSWORD_HINT_ID} password={password} />}
        </Field>
      </div>

      {formAlert}

      <div className="au-actions">
        <button type="submit" className="au-btn" disabled={busy}>
          {isSignUp ? 'Create account' : 'Sign in'}
        </button>
        {isSignUp && (
          <p className="au-fine-print">
            By creating an account you agree to our{' '}
            <TermsPageLink>
              <a>Terms of Use</a>
            </TermsPageLink>{' '}
            and{' '}
            <PrivacyPageLink>
              <a>Privacy Policy</a>
            </PrivacyPageLink>
            .
          </p>
        )}
        <Divider />
        {amazon}
      </div>

      <p className="au-switch">
        {isSignUp ? 'Already have an account?' : 'New to Parabo?'}{' '}
        <button
          type="button"
          className="au-link"
          onClick={() => switchMode(isSignUp ? AUTH_MODE.SIGN_IN : AUTH_MODE.SIGN_UP)}
        >
          {isSignUp ? 'Sign in' : 'Create an account'}
        </button>
      </p>
    </form>
  );
};

AuthPanel.propTypes = {
  initialMode: PropTypes.string,
  countryCode: PropTypes.string,
  // The Amazon button, rendered by the host so it keeps the redeem flow.
  amazon: PropTypes.node,
  onSignedIn: PropTypes.func.isRequired,
  onTrackError: PropTypes.func.isRequired,
  onTrack: PropTypes.func.isRequired,
};

export default AuthPanel;
